import React from "react";
import { caseState, gbp, isWorking, nowOf, stepOf } from "@/lib/stages";
import { ukDayTime } from "@/lib/time";
import { Btn } from "./kit";
import { SlaCountdown } from "./SlaCountdown";

/* The whole card is tinted in the case's stage colour — the same calm fills the portal always had. */
const TONE = {
  blue:  { bg: "#E4ECFF", bd: "#A9C2FF" },
  teal:  { bg: "#E6FBF7", bd: "#5EEAD4" },
  amber: { bg: "#FFF3DC", bd: "#E8BE7E" },
  mint:  { bg: "#D8F2E9", bd: "#8BD6BF" },
  green: { bg: "#0B6B4F", bd: "#0B6B4F" },
  slate: { bg: "#F4F6F9", bd: "#E2E8F0" },
};

/**
 * One plain-English line for an introducer, reconciling the whole panel into a single status.
 * The owner's complaint: the portal said one desk accepted while an email said another passed —
 * two true facts that read as a contradiction. This banner states the ONE thing that matters and
 * explains, in a calm line, that a desk passing is normal on a multi-desk panel.
 */
export function CaseStatusBanner({ lead, panel, onChase, chasing, chased, agent }) {
  const desks = panel?.desks || [];
  const offers = panel?.offers || [];
  const best = offers[0];
  const live = desks.filter((d) => d.status !== "declined");
  const passed = desks.filter((d) => d.status === "declined" && !d.withdrawn);
  const inReview = desks.filter((d) => d.status === "in_review");
  const working = desks.filter(isWorking);
  const stage = lead?.stage || "new";
  const cs = caseState(lead);
  const quiet = cs.key === "quiet";
  const caseSla = panel?.case_sla;
  const updates = live.filter((d) => nowOf(d));
  const upd = cs.now || (updates[0] && nowOf(updates[0]));
  const accepted = desks.filter((d) => stepOf(d) === "offer_accepted");
  const turnedDown = desks.filter((d) => stepOf(d) === "offer_declined");
  const paused = (n) => (live.length > n ? "That desk's clock is paused while it waits — the others keep going." : "The desk's clock is paused while it waits.");

  let tone = "amber", head = "Being packaged.", sub = "The Connexis desk is getting it ready for the panel.";
  let showChase = true, explain = false;

  if (stage === "completed") {
    tone = "green"; head = "Funded — this one's over the line.";
    sub = "The facility has completed. Your fee is confirmed."; showChase = false;
  } else if (accepted.length) {
    tone = "mint"; head = "Offer accepted — the funds are being arranged.";
    sub = "Your client has said yes to the offer, and the desk is arranging the funds now. We'll tell you the moment it completes.";
    showChase = false;
  } else if (["sent", "in_review"].includes(stage) && upd) {
    const phase = { awaiting_docs: "Awaiting documents", preparing_offer: "Preparing offer" }[cs.key] || "Under assessment";
    tone = cs.key === "awaiting_docs" ? "amber" : cs.key === "preparing_offer" ? "teal" : "blue";
    head = `${phase} — right now: ${upd.label.toLowerCase()}.`;
    sub = `${{
      no_contact: "The desk is calling, texting and emailing to get hold of your client. A quick word from you will get it moving — ask them to look out for a call, text or email and reply.",
      callback: `A call back is booked for ${ukDayTime(upd.callback_at, "a set time")} (UK time). Make sure your client is free to take it.`,
      credit: "The lender's credit team has it for a decision. Terms usually follow when credit says yes.",
      valuation: "A valuation or searches are under way. The decision follows once the results are back.",
    }[upd.key]} Next step: ${upd.next.replace(/[.!?]+$/, "")}.${upd.pauses ? ` ${paused(updates.length)}` : ""}`;
    explain = passed.length > 0 || live.length > 1;
  } else if (cs.key === "preparing_offer") {
    const allIn = cs.ctx?.auto_prep;
    tone = "teal"; head = allIn ? "Preparing offer — every document is in." : "Preparing offer — the desk is drawing up terms.";
    sub = `${allIn ? "The desk has everything it asked for and is drawing up terms now." : "The desk has what it needs and is working on its terms."} We'll tell you the moment the offer lands — nothing for you to do.`;
    explain = passed.length > 0 || live.length > 1;
    showChase = false;
  } else if (cs.key === "awaiting_docs") {
    tone = "amber"; head = "Awaiting documents — the desk has asked for paperwork.";
    sub = `${lead?.docs_open > 0 ? `${lead.docs_open} document request${lead.docs_open === 1 ? " is" : "s are"} open — each item below says exactly what's still needed. ` : ""}Connie checks every upload against it and ticks off what matches. Press Chase client and we'll text and email your client a private upload link. Once everything is in, the case moves to Preparing offer by itself.`;
    explain = passed.length > 0 || live.length > 1;
    showChase = false;
  } else if (stage === "offer_made" && !best && turnedDown.length) {
    tone = "slate"; head = "Offer issued — your client turned the terms down, and the desk can come back with new ones.";
    sub = "That's not the end of it: the desk can revise its terms, or pass. If you know what would work for your client — a lower rate, a longer term, a different fee — add it to the case thread and we'll pass it on.";
    showChase = false;
    explain = live.length > turnedDown.length;
  } else if (stage === "offer_made" || best) {
    tone = "mint"; head = "You've got an offer on the table.";
    const bits = [best?.rate_pct != null ? `${best.rate_pct}%` : null,
      best?.term_months ? `over ${best.term_months} months` : null].filter(Boolean).join(" ");
    sub = best
      ? `A desk has come back with terms${bits ? ` — ${gbp(best.amount_gbp || lead.amount_gbp)} ${bits}` : ""}. Your client can take it from here.`
      : "A desk has come back with terms. Your client can take it from here.";
    showChase = false;
  } else if (stage === "declined") {
    tone = "slate"; head = "The desks on it have passed — we're finding a new home.";
    sub = "Nothing has been said to your client; that call is yours. A pass isn't a rejection of the deal — we re-place it with other desks."; showChase = false;
  } else if (working.length) {
    tone = "teal"; head = "Under assessment — a desk is working on your case.";
    sub = `${working.length === 1 ? "A desk has" : `${working.length} desks have`} opened the pack and ${working.length === 1 ? "is" : "are"} actively moving on it — notes, documents or questions in the last 48 hours.`;
    explain = passed.length > 0 || live.length > 1;
    showChase = false;
  } else if (quiet) {
    tone = "amber"; head = "We're chasing the desks on this for you.";
    sub = `It's been quiet for a few days. Tap below and we'll push the desk${live.length === 1 ? "" : "s"} for an answer today.`;
    explain = passed.length > 0 || live.length > 1;
  } else if (inReview.length) {
    tone = "blue"; head = "Under assessment with the panel.";
    sub = `${inReview.length === 1 ? "A lender is" : `${inReview.length} lenders are`} reviewing your case right now.`;
    explain = passed.length > 0 || live.length > 1;
  } else if (live.length) {
    tone = "amber"; head = "With lenders now.";
    sub = `Your case is in front of ${live.length} desk${live.length === 1 ? "" : "s"} at the same time.`;
    explain = passed.length > 0 || live.length > 1;
  }

  const t = TONE[tone];
  const dark = tone === "green";

  return (
    <div data-testid="referrer-case-status"
         style={{ background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 16, padding: "20px 22px", marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ font: '800 17px "Instrument Sans"', color: dark ? "#fff" : "#0B1220", letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
            {tone === "teal" && <span className="cx-live-dot" data-testid="referrer-case-status-live" />}{head}
          </div>
          <div style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.5, color: dark ? "rgba(255,255,255,.85)" : "#334155", maxWidth: 580 }}>{sub}</div>
        </div>
        {showChase && !agent && (
          <Btn kind="primary" onClick={onChase} disabled={chasing || chased} testId="referrer-case-status-chase"
               title={chased ? "You've asked us today — we'll come back to you" : "Ask the desk for a decision now"}>
            {chasing ? "Asking the desk…" : chased ? "We're on it" : "Request a decision now"}
          </Btn>
        )}
      </div>
      {showChase && <SlaCountdown sla={caseSla} variant="introducer" testId="referrer-case-sla" />}
      {explain && (
        <div data-testid="referrer-case-status-explain"
             style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(11,18,32,.08)", fontSize: 12.5, lineHeight: 1.5, color: "var(--mut)" }}>
          Your case goes to several desks at once. A desk passing is normal — you only need <b>one yes</b>, and one desk
          passing never affects the others.
          {passed.length > 0 && (
            <> So far {passed.length} {passed.length === 1 ? "desk has" : "desks have"} passed and {live.length} {live.length === 1 ? "is" : "are"} still looking.</>
          )}
        </div>
      )}
    </div>
  );
}
